// Firebase 디버깅 도구 (개발 환경 전용)
import { useEffect } from 'react';
import { debugCompleteFlow, repairPriceData, comparePriceIdGeneration, testFirebaseWrite } from '../../../../utils/priceDebugging';
import { collection, doc, setDoc, getDocs } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { generatePriceId } from '../../../../types/deviceCatalog';
import { getUnifiedPriceService } from '../../../../services/unifiedPriceAdapter';
import { getDeviceCatalogService } from '../../../../services/deviceCatalogAdapter';

// 개발 환경에서만 Firebase 디버깅 도구들을 window 객체에 등록
export const useFirebaseDebug = () => {
  useEffect(() => {
    // 개발 환경에서만 디버깅 도구 활성화
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
      
      // 🧪 Firebase 가격 조회 테스트 함수 (브라우저 콘솔용)
      const testFirebasePriceQuery = async (modelName: string, storage: string, connectivity?: string) => {
        console.log('🧪 Firebase 가격 조회 테스트 시작');
        console.log(`🎯 대상: ${modelName} ${storage} ${connectivity || 'Wi-Fi'}`);
        
        try {
          // 1단계: unified_prices 전체 조회
          const UnifiedPriceService = getUnifiedPriceService();
          const allPrices = await UnifiedPriceService.getPricesForManagement('Apple', 'tablet') as any[];
          
          console.log(`📊 unified_prices 전체: ${allPrices.length}개`);
          
          // 2단계: 해당 모델 관련 가격 찾기
          const relatedPrices = allPrices.filter((price: any) => 
            price.modelId?.toLowerCase().includes(modelName.toLowerCase().replace(/\s+/g, ''))
          );
          
          console.log(`🔍 관련 가격 데이터: ${relatedPrices.length}개`);
          relatedPrices.forEach((price: any) => {
            console.log(`  - ${price.modelId} | ${price.variant?.storage} | ${price.variant?.connectivity || 'Wi-Fi'}`);
            console.log(`    가격: A=${price.gradePrices?.A} B=${price.gradePrices?.B} C=${price.gradePrices?.C} D=${price.gradePrices?.D}`);
          });
          
          // 3단계: 정확한 매칭
          const exactMatch = relatedPrices.find((price: any) =>
            price.variant?.storage === storage &&
            (price.variant?.connectivity || 'Wi-Fi') === (connectivity || 'Wi-Fi')
          );
          
          if (exactMatch) {
            console.log(`✅ 정확한 매칭 발견!`);
            console.log(`💰 가격: A=${exactMatch.gradePrices?.A} B=${exactMatch.gradePrices?.B} C=${exactMatch.gradePrices?.C} D=${exactMatch.gradePrices?.D}`);
            return exactMatch.gradePrices;
          } else {
            console.log(`❌ 정확한 매칭 없음`);
            return null;
          }
          
        } catch (error) {
          console.error('❌ 테스트 실패:', error);
          return null;
        }
      };

      // 🔥 Firebase 데이터 직접 조회 함수 (브라우저 콘솔용)
      const debugFirebaseData = async () => {
        console.log('🔥 Firebase 데이터 직접 조회 및 분석');
        
        try {
          // 1단계: device_catalog 조회
          const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
          const catalogModels = catalogSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const appleTablets = catalogModels.filter((m: any) => m.deviceType === 'tablet' && m.brand === 'Apple');
          
          console.log(`📱 device_catalog Apple 태블릿: ${appleTablets.length}개`);
          console.log('🍎 Apple 태블릿 샘플:');
          appleTablets.slice(0, 3).forEach((tablet: any) => {
            console.log(`  - ID: ${tablet.id}`);
            console.log(`    모델: ${tablet.modelKo} (${tablet.series})`);
            console.log(`    스토리지: ${tablet.storageOptions?.join(', ')}`);
            console.log(`    연결성: ${tablet.connectivityOptions?.join(', ')}`);
          });
          
          // 2단계: unified_prices 조회
          const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
          const allPrices = pricesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const appleTabletPrices = allPrices.filter((p: any) => p.deviceType === 'tablet' && p.brand === 'Apple');
          
          console.log(`\n💰 unified_prices Apple 태블릿: ${appleTabletPrices.length}개`);
          console.log('💰 Apple 태블릿 가격 샘플:');
          appleTabletPrices.slice(0, 5).forEach((price: any) => {
            console.log(`  - ID: ${price.id}`);
            console.log(`    모델ID: ${price.modelId}`);
            console.log(`    variant: ${JSON.stringify(price.variant)}`);
            console.log(`    가격: A=${price.gradePrices?.A} B=${price.gradePrices?.B} C=${price.gradePrices?.C} D=${price.gradePrices?.D}`);
          });
          
        } catch (error) {
          console.error('❌ Firebase 데이터 조회 실패:', error);
        }
      };

      // 기본 디버깅 함수들 window 객체에 등록
      (window as any).testFirebasePriceQuery = testFirebasePriceQuery;
      (window as any).debugFirebaseData = debugFirebaseData;
      (window as any).debugCompleteFlow = debugCompleteFlow;
      (window as any).repairPriceData = repairPriceData;
      (window as any).comparePriceIdGeneration = comparePriceIdGeneration;
      (window as any).testFirebaseWrite = testFirebaseWrite;
      
      // 간편 테스트 함수
      (window as any).testPrice = (modelName: string, storage: string, connectivity = 'Wi-Fi') => {
        console.log(`🧪 간편 가격 테스트: ${modelName} ${storage} ${connectivity}`);
        return testFirebasePriceQuery(modelName, storage, connectivity);
      };

      // 🔍 간편 사용법 가이드
      (window as any).helpPriceDebugging = () => {
        console.log('🔥 가격 디버깅 도구 사용법');
        console.log('==========================================');
        console.log('');
        console.log('📋 사용 가능한 함수들:');
        console.log('');
        console.log('🍎 Apple 태블릿:');
        console.log('  debugCompleteFlow("아이패드 모델명", "용량", "연결성")');
        console.log('  testPrice("아이패드 프로 11인치 M4", "256GB", "Wi-Fi")');
        console.log('');
        console.log('🤖 Samsung 태블릿:');
        console.log('  testPrice("갤럭시 탭 S9", "256GB")');
        console.log('  debugCompleteFlow("갤럭시 탭 S9 울트라", "512GB", "Wi-Fi")');
        console.log('');
        console.log('🔧 가격 설정 문제 해결:');
        console.log('  repairPriceData("모델명", "용량", "연결성", {A:가격, B:가격, C:가격, D:가격})');
        console.log('');
        console.log('🧪 빠른 테스트:');
        console.log('  testPrice("모델명", "용량", "연결성")');
        console.log('  testFirebaseWrite("모델명", "용량", "연결성", 가격객체)');
        console.log('');
      };

      console.log('🔥 Firebase 디버깅 도구가 개발 환경에서 활성화되었습니다.');
      console.log('💡 사용법: helpPriceDebugging() 실행');
    }
  }, []);
};
